// src/screens/seller/orders/OrdersManagement.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import {
  FiSearch,
  FiX,
  FiStar,
  FiClipboard,
  FiAlertCircle,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { toast, ToastContainer } from "react-toastify";
import { useTheme } from "../../../contexts/ThemeContext";
import SellerMasterLayout from "../../../layouts/seller/SellerMasterLayout";
import AppContext from "../../../contexts/AppContext";
import "../../../assets/css/seller/screens/SellerOrderManagement.css";
import WalletBlockedModal from "../../../components/seller/walletblockedmodal/WalletBlockedModal";

const StarRating = ({ rating, size = 12 }) => (
  <div className="sor-review-stars">
    {[1, 2, 3, 4, 5].map((star) => (
      <FiStar
        key={star}
        size={size}
        className={star <= rating ? "sor-star-filled" : "sor-star-empty"}
      />
    ))}
  </div>
);

const ReviewModal = ({ review, customerName, orderId, onClose, baseUrlForFiles }) => {
  if (!review) return null;
  return (
    <>
      <div className="sor-review-modal-backdrop" onClick={onClose} />
      <div className="sor-review-modal">
        <div className="sor-review-modal-header">
          <div>
            <h3 className="sor-review-modal-title">Customer Review</h3>
            <span className="sor-review-modal-order">
              {orderId} — {customerName}
            </span>
          </div>
          <button className="sor-review-modal-close" onClick={onClose}>
            <FiX size={18} />
          </button>
        </div>
        <div className="sor-review-modal-meta">
          <StarRating rating={review.rating} size={16} />
          <span className="sor-review-modal-date">
            {review.created_at?.slice(0, 10)}
          </span>
        </div>

        {review.images?.length > 0 && (
          <div className="sor-review-modal-gallery">
            {review.images.map((img) => (
              <img
                key={img.id}
                src={`${baseUrlForFiles}/${img.image_path}`}
                alt="review attachment"
                className="sor-review-modal-image"
              />
            ))}
          </div>
        )}

        <p className="sor-review-modal-comment">"{review.comment}"</p>
      </div>
    </>
  );
};

const ReviewBadge = ({ review, onClick, baseUrlForFiles }) => {
  if (!review) return <span className="sor-no-review-badge">No Review</span>;
  const firstImage = review.images?.[0]?.image_path;
  const extraCount = (review.images?.length || 0) - 1;

  return (
    <button className="sor-review-badge-btn" onClick={onClick}>
      <div className="sor-review-badge-top">
        {firstImage && (
          <div className="sor-review-thumb-wrap">
            <img
              src={`${baseUrlForFiles}/${firstImage}`}
              alt="review"
              className="sor-review-thumb"
            />
            {extraCount > 0 && (
              <span className="sor-review-thumb-more">+{extraCount}</span>
            )}
          </div>
        )}
        <StarRating rating={review.rating} />
      </div>
      <span className="sor-review-excerpt">
        "{review.comment?.slice(0, 28)}
        {review.comment?.length > 28 ? "…" : ""}"
      </span>
    </button>
  );
};

const STATUS_OPTIONS = [
  "All Status",
  "Processing",
  "Shipped",
  "completed",
  "Cancelled",
];

const SellerOrdersManagement = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const { baseUrl, baseUrlForFiles, setSellerSelectedOrder, sellerAuthToken } =
    useContext(AppContext);
  const [walletBlocked, setWalletBlocked] = useState(false);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [reviewModal, setReviewModal] = useState(null);
  const [statsData, setStatsData] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const getSellerOrders = () => {
    setLoading(true);
    const seller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const formdata = new FormData();
    formdata.append("seller_id", seller?.id);
    formdata.append("page", currentPage);
    axios
      .post(`${baseUrl}/seller/order/getSellerOrders`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${sellerAuthToken}`,
        },
      })
      .then((response) => {
        setOrders(response.data.orders ?? []);
        setTotalPages(response.data.last_page);
        setTotalItems(response.data.total);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  // ── YEH FUNCTION MISSING HEADER KI WAJAH SE 401 DE RAHA THA ──
  const getStats = () => {
    const seller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const fd = new FormData();
    fd.append("seller_id", seller?.id);
    axios
      .post(`${baseUrl}/seller/order/stats`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${sellerAuthToken}`,
        },
      })
      .then((res) => setStatsData(res.data))
      .catch((err) => {
        console.log("STATS API ERROR:", err.response?.data || err.message);
      });
  };

  useEffect(() => {
    if (sellerAuthToken) {
      getSellerOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sellerAuthToken]);

  useEffect(() => {
    if (sellerAuthToken) {
      getStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerAuthToken]);

  const handleViewOrder = (order) => {
    const seller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const formdata = new FormData();
    formdata.append("seller_id", seller?.id);
    axios
      .post(`${baseUrl}/seller/order/checkWalletAccess`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${sellerAuthToken}`,
        },
      })
      .then((res) => {
        if (res.data.can_view) {
          setSellerSelectedOrder(order);
          navigate("/seller/orders/order-detail");
        } else {
          setWalletBlocked(true);
        }
      })
      .catch(() => toast.error("Something went wrong."));
  };

  const getFilteredOrders = () => {
    let filtered = orders;
    if (statusFilter !== "All Status") {
      filtered = filtered.filter((o) => o.status === statusFilter);
    }
    if (search.trim()) {
      filtered = filtered.filter(
        (o) =>
          o.id?.toString().includes(search) ||
          o.full_name?.toLowerCase().includes(search.toLowerCase()),
      );
    }
    return filtered;
  };

  const filteredOrders = getFilteredOrders();
  const hasCompletedOrders = filteredOrders.some(
    (o) => o.status === "completed",
  );

  const getVisiblePageNumbers = () => {
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    const pages = [];
    for (let page = startPage; page <= endPage; page++) pages.push(page);
    return pages;
  };

  return (
    <SellerMasterLayout>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className={`sor-shell ${isDark ? "dark-mode" : ""}`}>
        <main className="sor-main">
          <section className="sor-card-shell">
            <div className="sor-overview">
              <div className="sor-overview-card">
                <div>
                  <span className="sor-overview-label">Total Orders</span>
                  <h3 className="sor-overview-value">
                    {statsData?.total_orders ?? "0"}
                  </h3>
                  <p className="sor-overview-sub">All received orders</p>
                </div>
                <div className="sor-overview-icon">
                  <FiClipboard />
                </div>
              </div>
              <div className="sor-overview-card">
                <div>
                  <span className="sor-overview-label">Completed</span>
                  <h3 className="sor-overview-value">
                    {statsData?.completed_orders ?? "0"}
                  </h3>
                  <p className="sor-overview-sub">Successfully delivered</p>
                </div>
                <div className="sor-overview-icon">
                  <FiCheckCircle />
                </div>
              </div>
              <div className="sor-overview-card">
                <div>
                  <span className="sor-overview-label">Cancelled</span>
                  <h3 className="sor-overview-value">
                    {statsData?.cancelled_orders ?? "0"}
                  </h3>
                  <p className="sor-overview-sub">Needs attention</p>
                </div>
                <div className="sor-overview-icon sor-overview-icon-alert">
                  <FiAlertCircle />
                </div>
              </div>
            </div>

            <div className="sor-header">
              <div className="sor-header-left" />
              <div className="sor-header-filters">
                <div className="sor-search">
                  <FiSearch size={15} />
                  <input
                    placeholder="Search orders"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      className="sor-clear-input-btn"
                      onClick={() => setSearch("")}
                    >
                      <FiX size={12} />
                    </button>
                  )}
                </div>
                <select
                  className="sor-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="sor-table-wrapper">
              <table className="sor-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Date</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Status</th>
                    {hasCompletedOrders && <th>Review</th>}
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td
                        colSpan={hasCompletedOrders ? 8 : 7}
                        className="text-center py-4"
                      >
                        <Spinner
                          animation="border"
                          size="sm"
                          className="sor-spinner"
                          style={{ color: "#ff8559" }}
                        />
                        <span className="ms-2 sor-muted sor-loading-text">
                          Loading orders...
                        </span>
                      </td>
                    </tr>
                  )}

                  {!loading && filteredOrders.length === 0 && (
                    <tr>
                      <td
                        colSpan={hasCompletedOrders ? 8 : 7}
                        className="sor-no-results-row"
                      >
                        No orders found.
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    filteredOrders.length > 0 &&
                    filteredOrders.map((order) => (
                      <tr key={order.id}>
                        <td className="sor-order-id-cell">#{order.id}</td>
                        <td>
                          <div className="sor-cell-main">
                            <div className="sor-thumb sor-thumb-orange">
                              {order.full_name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="sor-cell-title">
                                {order.full_name}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td>
                          {new Date(order.created_at).toLocaleDateString(
                            "en-PK",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )}
                        </td>
                        <td>
                          {(order.cart || []).reduce(
                            (sum, i) => sum + i.quantity,
                            0,
                          )}
                        </td>
                        <td className="sor-total-cell">
                          Rs {Number(order.total_amount).toLocaleString()}
                        </td>
                        <td>
                          <span
                            className={`sor-status ${
                              order.status === "Processing"
                                ? "sor-status-processing"
                                : order.status === "Shipped"
                                  ? "sor-status-shipped"
                                  : order.status === "completed"
                                    ? "sor-status-delivered"
                                    : order.status === "Cancelled"
                                      ? "sor-status-cancel"
                                      : ""
                            }`}
                          >
                            {order.status}
                          </span>
                        </td>
                        {hasCompletedOrders && (
                          <td>
                            {order.status === "completed" ? (
                              <ReviewBadge
                                review={order.review || null}
                                baseUrlForFiles={baseUrlForFiles}
                                onClick={() =>
                                  order.review &&
                                  setReviewModal({
                                    review: order.review,
                                    customerName: order.full_name,
                                    orderId: `#${order.id}`,
                                  })
                                }
                              />
                            ) : (
                              <span className="sor-review-na-badge">—</span>
                            )}
                          </td>
                        )}
                        <td>
                          <button
                            className="sor-link"
                            onClick={() => handleViewOrder(order)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {!loading && filteredOrders.length > 0 && totalPages > 1 && (
                <div className="sor-pagination-bar">
                  <span className="sor-pagination-info">
                    Showing {(currentPage - 1) * itemsPerPage + 1}-
                    {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
                  </span>
                  <div className="sor-pagination-controls">
                    <button
                      className="sor-page-btn sor-page-btn--nav"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      <FiChevronLeft size={14} />
                    </button>
                    {getVisiblePageNumbers().map((page) => (
                      <button
                        key={page}
                        className={`sor-page-btn ${page === currentPage ? "sor-page-btn--active" : ""}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      className="sor-page-btn sor-page-btn--nav"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      <FiChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        </main>
      </div>

      {reviewModal && (
        <ReviewModal
          review={reviewModal.review}
          customerName={reviewModal.customerName}
          orderId={reviewModal.orderId}
          baseUrlForFiles={baseUrlForFiles}
          onClose={() => setReviewModal(null)}
        />
      )}
      {walletBlocked && (
        <WalletBlockedModal onClose={() => setWalletBlocked(false)} />
      )}
    </SellerMasterLayout>
  );
};

export default SellerOrdersManagement;